import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  getCardsByOwner,
  getMyOwnerCards,
  getMyOrders,
  getBatchCardImages,
  getUserByWallet,
  getMe,
  getNotifications,
  getMyMintCredits,
  getMyPendingCustodyRequests,
  getMyPendingOnChainCustody,
  getMyLabCustodyClaims,
  createLabCustodyClaim,
  getBurnRequests,
} from '../services/api'
import { getFlowConfig } from '../utils/cardFlowConfig'
import CardJourney from '../components/CardJourney'
import CardFlowModal from '../components/CardFlowModal'
import CustodyOnChainHandoff from '../components/CustodyOnChainHandoff'
import Section from '../components/m1nt/Section'
import OwnerBackdrop from '../components/m1nt/OwnerBackdrop'
import OwnerSignedOut from '../components/m1nt/OwnerSignedOut'
import { M1ntButton, M1ntNotice } from '../components/m1nt/ui'
import SubmitCardModal from '../components/SubmitCardModal'
import { announceMintCredits } from '../utils/mintCreditsSync'
import { useAuthSession } from '../contexts/AuthSessionContext'

/** Build list of { card, order } for owner. Each card appears once with its latest order. */
function buildCardFlowItems(cards, ordersAsOwner) {
  const list = []
  const orderList = ordersAsOwner || []
  const cardIdsSeen = new Set()

  for (const card of cards) {
    const matchingOrders = orderList.filter(
      (o) => o && (o.cardId === card.id || o.card?.id === card.id)
    )
    const latestOrder =
      matchingOrders.length > 0
        ? matchingOrders.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
        : null
    list.push({ card, order: latestOrder })
    cardIdsSeen.add(card.id)
  }

  // Include orders whose card is only in order.card (e.g. not yet in cards list)
  for (const order of orderList) {
    const card = order.card
    if (!card?.id || cardIdsSeen.has(card.id)) continue
    cardIdsSeen.add(card.id)
    list.push({ card, order })
  }

  return list
}

/** True when the owner has something to do (pay, ship, custody approval, etc.). */
function needsOwnerAction(item) {
  if (item?.card?.status === 'BURNED') return false
  if (item?.custodyRequest) return true
  const config = getFlowConfig(item?.order?.status)
  return !!config?.ownerAction
}

/** In progress but no owner action right now (waiting on lab/shipping/etc). */
function isInProgressNoAction(item) {
  if (item?.card?.status === 'BURNED') return false
  if (item?.custodyRequest) return false
  const status = item?.order?.status
  if (status === 'DELIVERED' || status === 'CANCELLED') return false
  const config = getFlowConfig(status)
  return !config?.ownerAction
}

export default function OwnerDashboard() {
  const { wallet, user: sessionUser, emailAuthToken, onboardingToken } = useAuthSession()
  const location = useLocation()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [cards, setCards] = useState([])
  const [orders, setOrders] = useState({ asOwner: [] })
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  /** Default to Concluded so /owner opens on the main collection view (not “all” / mint tile focus). */
  const [flowFilter, setFlowFilter] = useState('done') // 'all' | 'action' | 'progress' | 'done'
  const [selectedItem, setSelectedItem] = useState(null) // { card, order }
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [cardImages, setCardImages] = useState({})
  const [loadingImages, setLoadingImages] = useState(false)
  /** Notification deep link: open chain-of-custody for this card on /owner */
  const [ownerNotificationFocusCardId, setOwnerNotificationFocusCardId] = useState(null)
  const [custodyPending, setCustodyPending] = useState([])
  const [labCustodyClaims, setLabCustodyClaims] = useState([])
  const [custodyOnChainPending, setCustodyOnChainPending] = useState([])
  const [custodyOnChainMeta, setCustodyOnChainMeta] = useState({})
  const [mintCredits, setMintCredits] = useState(0)
  const [mintCreditGateError, setMintCreditGateError] = useState('')
  const [pendingBurnRequests, setPendingBurnRequests] = useState([])

  const clearOwnerNotificationFocus = useCallback(() => {
    setOwnerNotificationFocusCardId(null)
  }, [])

  const clearAllData = () => {
    setUser(null)
    setCards([])
    setOrders({ asOwner: [] })
    setNotifications([])
    setCustodyPending([])
    setLabCustodyClaims([])
    setMintCredits(0)
    setPendingBurnRequests([])
    setCardImages({})
    setSelectedItem(null)
    setLoading(false)
  }

  useEffect(() => {
    if (onboardingToken) return
    if (wallet || emailAuthToken) {
      loadData()
    } else {
      clearAllData()
    }
  }, [wallet, emailAuthToken, onboardingToken])

  useEffect(() => {
    if (onboardingToken || (!wallet && !emailAuthToken)) return
    let cancelled = false
    const refreshMintCredits = async () => {
      try {
        const res = await getMyMintCredits(wallet || null)
        if (cancelled) return
        const nextCredits = Number(res?.data?.data?.mintCredits || 0)
        setMintCredits(nextCredits)
        announceMintCredits(nextCredits)
      } catch {
        // Keep current value when background refresh fails.
      }
    }

    const interval = setInterval(refreshMintCredits, 5000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [wallet, emailAuthToken, onboardingToken])

  /** Refresh when returning to the tab so status updates from Lab/Grader show without a manual reload. */
  useEffect(() => {
    if (onboardingToken || (!wallet && !emailAuthToken)) return
    let hiddenAt = 0
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      if (hiddenAt && Date.now() - hiddenAt >= 2500) {
        loadData()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [wallet, emailAuthToken, onboardingToken])

  useEffect(() => {
    const handleProfilePictureUpdate = (event) => {
      if (event.detail?.user) setUser(event.detail.user)
    }
    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate)
    return () => window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate)
  }, [])

  useEffect(() => {
    if (location.state?.openSubmitModal && (wallet || emailAuthToken)) {
      const parsedCredits = Number(mintCredits)
      const canStartSubmission = Number.isFinite(parsedCredits) && parsedCredits > 0
      if (canStartSubmission) {
        setIsSubmitModalOpen(true)
      } else {
        setMintCreditGateError('You need at least 1 M1NT credit to start a submission.')
      }
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, wallet, emailAuthToken, navigate, location.pathname, mintCredits])

  useEffect(() => {
    const id = location.state?.openOwnerCardId
    if (!id || (!wallet && !emailAuthToken) || loading) return

    const fullList = buildCardFlowItems(cards, orders.asOwner).filter(
      (item) => !isBurntCardItem(item)
    )
    const item = fullList.find((i) => i.card?.id === id)
    if (item && needsOwnerAction(item)) setFlowFilter('action')
    else setFlowFilter('all')

    if (item) setOwnerNotificationFocusCardId(id)
    navigate(location.pathname, { replace: true, state: {} })
  }, [
    loading,
    wallet,
    emailAuthToken,
    location.state?.openOwnerCardId,
    navigate,
    location.pathname,
    cards,
    orders.asOwner,
  ])

  const loadData = async () => {
    if (onboardingToken) return null
    const uid = sessionUser?.id
    if (!wallet && !emailAuthToken) return null
    if (!wallet && emailAuthToken && !uid) return null
    setLoading(true)
    try {
      const ordersParams = wallet ? { wallet } : { userId: uid }
      const notifyWallet = wallet || null
      const [
        cardsRes,
        ordersRes,
        userRes,
        notifRes,
        custodyRes,
        labClaimsRes,
        onChainRes,
        creditRes,
        burnRes,
      ] = await Promise.all([
        wallet ? getCardsByOwner(wallet) : getMyOwnerCards(),
        getMyOrders(ordersParams),
        wallet ? getUserByWallet(wallet).catch(() => null) : getMe().catch(() => null),
        getNotifications(notifyWallet, 50).catch(() => ({ data: { data: [] } })),
        getMyPendingCustodyRequests(notifyWallet).catch(() => ({ data: { data: [] } })),
        getMyLabCustodyClaims().catch(() => ({ data: { data: [] } })),
        getMyPendingOnChainCustody(notifyWallet).catch(() => ({ data: { data: [], meta: {} } })),
        getMyMintCredits(notifyWallet).catch(() => ({ data: { data: { mintCredits: 0 } } })),
        getBurnRequests('PENDING').catch(() => ({ data: { data: [] } })),
      ])
      const loadedCards = cardsRes.data.data || []
      setCards(loadedCards)
      const loadedOrders = ordersRes.data.data || { asOwner: [] }
      setOrders(loadedOrders)
      setNotifications(notifRes.data?.data || [])
      setCustodyPending(custodyRes.data?.data || [])
      setLabCustodyClaims(labClaimsRes.data?.data || [])
      setCustodyOnChainPending(onChainRes.data?.data || [])
      setCustodyOnChainMeta(onChainRes.data?.meta || {})
      const loadedCredits = Number(creditRes.data?.data?.mintCredits || 0)
      setMintCredits(loadedCredits)
      announceMintCredits(loadedCredits)
      setPendingBurnRequests(Array.isArray(burnRes.data?.data) ? burnRes.data.data : [])
      if (userRes?.data?.data) setUser(userRes.data.data)

      const orderCards = loadedOrders.asOwner?.map((o) => o.card).filter(Boolean) || []
      const allCards = [...loadedCards, ...orderCards]
      const uniqueCards = Array.from(new Map(allCards.map((c) => [c.id, c])).values())

      if (uniqueCards.length > 0) loadCardImages(uniqueCards)
      return { loadedOrders }
    } catch (error) {
      console.error('Failed to load data:', error)
      return null
    } finally {
      setLoading(false)
    }
  }

  const loadCardImages = async (cardsList) => {
    if (!cardsList?.length) return
    setLoadingImages(true)
    try {
      const res = await getBatchCardImages(cardsList.map((c) => c.id))
      setCardImages(res.data.data?.images || {})
    } catch (err) {
      console.error('Failed to load card images:', err)
    } finally {
      setLoadingImages(false)
    }
  }

  const handleSubmitSuccess = () => {
    loadData()
  }

  const parsedCredits = Number(mintCredits)
  const canStartSubmission = Number.isFinite(parsedCredits) && parsedCredits > 0

  const handleOpenSubmitModal = () => {
    if (!canStartSubmission) {
      setMintCreditGateError('You need at least 1 M1NT credit to start a submission.')
      return
    }
    setMintCreditGateError('')
    setIsSubmitModalOpen(true)
  }

  const handleSelectCard = (item) => {
    if (item?.card) {
      setSelectedItem({
        card: item.card,
        order: item.order,
        custodyRequest: item.custodyRequest ?? null,
      })
    }
  }

  const handleActionComplete = async () => {
    if (!wallet && !emailAuthToken) return
    const orderId = selectedItem?.order?.id
    const cardFallback = selectedItem?.card
    const result = await loadData()
    if (orderId && result?.loadedOrders?.asOwner) {
      const freshOrder = result.loadedOrders.asOwner.find((o) => o.id === orderId)
      if (freshOrder) {
        setSelectedItem({
          card: freshOrder.card || cardFallback,
          order: freshOrder,
        })
      }
    }
  }

  const handleRequestLabCustodyClaim = async (cardId, newOwnerWallet) => {
    if (!cardId || !newOwnerWallet) {
      return { ok: false, error: 'Wallet address is required.' }
    }
    try {
      await createLabCustodyClaim({ cardId, newOwnerWallet })
      await loadData()
      return { ok: true }
    } catch (err) {
      return {
        ok: false,
        error: err.response?.data?.error || err.message || 'Could not submit custody request.',
      }
    }
  }

  const burnSecondApprovalByCardId = useMemo(() => {
    const m = {}
    for (const br of pendingBurnRequests) {
      if (br.status !== 'PENDING') continue
      const n = Array.isArray(br.approvals) ? br.approvals.length : 0
      if (n < 2) m[br.cardId] = true
    }
    return m
  }, [pendingBurnRequests])

  const cardFlowItems = useMemo(() => {
    const map = Object.fromEntries(custodyPending.map((r) => [r.cardId, r]))
    const labMap = Object.fromEntries(
      (labCustodyClaims || []).filter((r) => r?.status === 'PENDING_LAB').map((r) => [r.cardId, r])
    )
    return buildCardFlowItems(cards, orders.asOwner).map((item) => ({
      ...item,
      custodyRequest: map[item.card?.id] || null,
      labCustodyClaim: labMap[item.card?.id] || null,
      burnPendingAwaitingSecondApproval: Boolean(burnSecondApprovalByCardId[item.card?.id]),
    }))
  }, [cards, orders.asOwner, custodyPending, labCustodyClaims, burnSecondApprovalByCardId])

  const stats = {
    actionNeeded:
      cardFlowItems.filter(needsOwnerAction).length + (custodyOnChainPending?.length || 0),
    inProgress: cardFlowItems.filter(isInProgressNoAction).length,
    done: cardFlowItems.filter(
      (item) => item.order?.status === 'DELIVERED' && item.card?.status !== 'BURNED'
    ).length,
  }

  const getFilteredItems = () => {
    if (flowFilter === 'all') return cardFlowItems
    if (flowFilter === 'action') return cardFlowItems.filter(needsOwnerAction)
    if (flowFilter === 'progress') return cardFlowItems.filter(isInProgressNoAction)
    if (flowFilter === 'done')
      return cardFlowItems.filter((item) => item.order?.status === 'DELIVERED')
    return cardFlowItems
  }

  const filteredItems = getFilteredItems()

  // Always show "in process" items first (anything not delivered/cancelled).
  const sortedFilteredItems = [...filteredItems].sort((a, b) => {
    const aStatus = a?.order?.status
    const bStatus = b?.order?.status
    const aDone = aStatus === 'DELIVERED' || aStatus === 'CANCELLED'
    const bDone = bStatus === 'DELIVERED' || bStatus === 'CANCELLED'
    if (aDone !== bDone) return aDone ? 1 : -1

    // Within the same group, sort by latest activity (order createdAt, fallback to card createdAt)
    const aTime = new Date(a?.order?.createdAt || a?.card?.createdAt || 0).getTime()
    const bTime = new Date(b?.order?.createdAt || b?.card?.createdAt || 0).getTime()
    return bTime - aTime
  })

  const hasUnreadForOrder = (orderId, cardId) =>
    notifications.some((n) => !n.read && (n.orderId === orderId || n.cardId === cardId))
  const ownerHasLinkedWallet = Boolean(String(user?.wallet || wallet || '').trim())

  const openLogin = () => window.dispatchEvent(new Event('openAuthModal'))

  if (!wallet && !emailAuthToken) {
    return (
      <OwnerSignedOut
        eyebrow="Owner portal"
        title="My Cards"
        subtitle="Log in to view your cards and orders."
        actions={
          <M1ntButton solid onClick={openLogin}>
            Log in
          </M1ntButton>
        }
        note="Sign in with your email or connect your wallet to see every card you own, follow its grading journey and approve custody transfers."
      />
    )
  }

  if (loading) {
    return (
      <Section first eyebrow="Owner portal" title="My Cards" subtitle="Loading your cards…">
        <div
          className="h-8 w-8 rounded-full border border-m1-line border-t-m1-violet"
          style={{ animation: 'm1nt-spin 0.9s linear infinite' }}
          role="status"
          aria-label="Loading your cards"
        />
      </Section>
    )
  }

  const FILTER_TITLES = {
    all: 'All cards',
    action: 'Action needed',
    progress: 'In progress',
    done: 'Concluded',
  }

  const statusOptions = [
    { key: 'all', label: 'All', count: cardFlowItems.length },
    { key: 'action', label: 'Action needed', count: cardFlowItems.filter(needsOwnerAction).length },
    { key: 'progress', label: 'In progress', count: stats.inProgress },
    {
      key: 'done',
      label: 'Concluded',
      count: cardFlowItems.filter((item) => item.order?.status === 'DELIVERED').length,
    },
  ]

  const statTiles = [
    {
      key: 'action',
      label: 'Action needed',
      value: stats.actionNeeded,
      attention: stats.actionNeeded > 0,
    },
    { key: 'progress', label: 'In progress', value: stats.inProgress },
    { key: 'done', label: 'Concluded', value: stats.done },
  ]

  return (
    <>
      <Section
        first
        backdrop={<OwnerBackdrop />}
        eyebrow="Owner portal"
        title="My Cards"
        subtitle="Manage your card collection, follow each grading journey and approve custody transfers."
      >
        {mintCreditGateError && (
          <M1ntNotice tone="amber" className="mb-8" role="alert">
            {mintCreditGateError}
          </M1ntNotice>
        )}

        <CustodyOnChainHandoff
          items={custodyOnChainPending}
          meta={custodyOnChainMeta}
          wallet={wallet}
          onComplete={() => loadData()}
        />

        <div
          role="group"
          aria-label="Collection overview"
          className="grid grid-cols-3 gap-px border border-m1-line bg-m1-line lg:grid-cols-4"
        >
          <button
            type="button"
            onClick={handleOpenSubmitModal}
            aria-disabled={!canStartSubmission}
            aria-label={
              canStartSubmission
                ? `M1NT, ${mintCredits} credits available. Submit a card`
                : 'M1NT, no credits available'
            }
            className={`col-span-3 flex min-h-[112px] flex-col justify-between p-4 text-left transition-colors sm:p-5 lg:col-span-1 ${
              canStartSubmission
                ? 'bg-white text-m1-ground hover:bg-[#e4e4e4]'
                : 'cursor-not-allowed bg-m1-surface text-m1-muted'
            }`}
          >
            <span className="font-m1-mono text-[11px] font-medium uppercase tracking-[0.14em]">
              M1NT
            </span>
            <span className="flex items-end justify-between gap-4">
              <span className="flex items-baseline gap-2">
                <span className="text-[36px] font-normal leading-none tabular-nums">
                  {mintCredits}
                </span>
                <span className="text-[13px]">{mintCredits === 1 ? 'credit' : 'credits'}</span>
              </span>
              <span className="text-[13px] font-medium">
                {canStartSubmission ? 'Submit a card' : 'No credits'}
              </span>
            </span>
          </button>
          {statTiles.map((tile) => {
            const selected = flowFilter === tile.key
            return (
              <button
                key={tile.key}
                type="button"
                onClick={() => setFlowFilter(tile.key)}
                aria-pressed={selected}
                className={`flex min-h-[112px] flex-col justify-between p-4 text-left transition-colors hover:bg-[#131217] sm:p-5 ${
                  selected ? 'bg-[#131217] shadow-[inset_0_-2px_0_0_#ffffff]' : 'bg-m1-ground'
                }`}
              >
                <span
                  className={`flex items-center gap-2 font-m1-mono text-[11px] uppercase tracking-[0.14em] ${
                    selected ? 'text-m1-ink' : 'text-m1-muted'
                  }`}
                >
                  {tile.label}
                  {tile.attention && (
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-m1-amber motion-safe:animate-pulse"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span className="text-[36px] font-normal leading-none tabular-nums text-m1-ink">
                  {tile.value}
                </span>
              </button>
            )
          })}
        </div>
      </Section>

      <Section
        id="collection"
        eyebrow="Collection"
        title={FILTER_TITLES[flowFilter] || 'All cards'}
        subtitle={
          filteredItems.length === 1
            ? '1 card in this view.'
            : `${filteredItems.length} cards in this view.`
        }
        actions={
          flowFilter !== 'all' ? (
            <M1ntButton onClick={() => setFlowFilter('all')}>Show all cards</M1ntButton>
          ) : null
        }
      >
        {filteredItems.length > 0 ? (
          <CardJourney
            items={sortedFilteredItems}
            statusOptions={statusOptions}
            statusFilter={flowFilter}
            onStatusChange={setFlowFilter}
            cardImages={cardImages}
            loadingImages={loadingImages}
            onOpenModal={handleSelectCard}
            onCustodyReview={handleSelectCard}
            hasUnreadForOrder={hasUnreadForOrder}
            onRequestLabCustodyClaim={handleRequestLabCustodyClaim}
            ownerHasLinkedWallet={ownerHasLinkedWallet}
            autoOpenCardId={ownerNotificationFocusCardId}
            onAutoOpenConsumed={clearOwnerNotificationFocus}
            onBeforeCardOpen={(item) => {
              if (item?.card?.status === 'BURNED' && item.card?.id) {
                navigate(`/card/${item.card.id}`)
                return true
              }
              return false
            }}
            directCardNavigation={
              flowFilter === 'done'
                ? (item) => item?.card?.id && navigate(`/card/${item.card.id}`)
                : undefined
            }
          />
        ) : (
          <div className="border border-m1-line px-6 py-14 text-center">
            <h3 className="font-m1-display text-[28px] font-normal text-m1-ink">
              {flowFilter !== 'all' ? `No cards in “${FILTER_TITLES[flowFilter]}”` : 'No cards yet'}
            </h3>
            <p className="mx-auto mt-3 max-w-[28em] text-[16px] font-light leading-relaxed text-m1-muted">
              {flowFilter !== 'all'
                ? 'No cards match this filter.'
                : 'Submit your first card for grading and authentication.'}
            </p>
          </div>
        )}
      </Section>

      <SubmitCardModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        wallet={wallet}
        ownerUserId={user?.id}
        onSuccess={handleSubmitSuccess}
      />

      {selectedItem && (
        <CardFlowModal
          card={selectedItem.card}
          order={selectedItem.order}
          imageUrl={cardImages[selectedItem.card?.id]?.frontImageUrl}
          notifications={notifications}
          wallet={wallet}
          custodyRequest={selectedItem.custodyRequest}
          onClose={() => setSelectedItem(null)}
          onActionComplete={handleActionComplete}
        />
      )}
    </>
  )
}
